import {
  Component,
  OnInit,
  OnDestroy,
  viewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { forkJoin, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { AuditDialogContainerComponent } from 'src/app/core/containers/audit-dialog-container.component';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import { Role } from 'src/app/model/admin/role';
import { AdminApiService } from 'src/app/services/api/admin-api.service';
import { AdminBuilderService } from 'src/app/services/builder/admin-builder.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { ROLES_BUSINESS_UNITS_TABLE_DEFINITION } from 'src/app/admin/definitions/data-table-definitions';
import { BusinessUnit } from 'src/app/model/admin/business-unit';
import { UpdateRoleInput } from 'src/app/model/admin/update-role-input';
import { sortByProperty } from 'src/app/utils/utils';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { RoleTableService } from 'src/app/services/table/role-table.service';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { FilterMetadata, PrimeTemplate } from '@openng/optimus-ui/api';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ProgressSpinnerComponent } from '../../../../core/components/progress-spinner.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { GeneralGridComponent } from '../../../../core/components/general-grid.component';
import { Dialog } from '@openng/optimus-ui/dialog';
import { InputText } from '@openng/optimus-ui/inputtext';
import { PickList } from '@openng/optimus-ui/picklist';

@Component({
  selector: 'app-role-business-unit-assign-container',
  templateUrl: './role-business-unit-assign-container.component.html',
  styleUrls: ['./role-business-unit-assign-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    ProgressSpinnerComponent,
    ButtonDirective,
    GeneralGridComponent,
    Dialog,
    FormsModule,
    ReactiveFormsModule,
    InputText,
    PickList,
    PrimeTemplate,
    TranslatePipe,
  ],
})
export class RoleBusinessUnitAssignContainerComponent
  extends SearchContainerComponent<Role>
  implements OnInit, OnDestroy
{
  protected _adminBuilder = inject(AdminBuilderService);
  protected _adminApi = inject(AdminApiService);
  protected _translateService = inject(TranslateService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _tabService = inject(TabService);
  protected _roleTableService = inject(RoleTableService);
  protected _fb = inject(UntypedFormBuilder);
  protected _authApi = inject(AuthApiService);

  readonly audit = viewChild<AuditDialogContainerComponent>('audit');

  canEdit = false;
  saving = false;
  roleBusinessUnitForm!: UntypedFormGroup;
  displayDialog = false;
  dialogHeader!: string;

  rolesAndBusinessUnits: any[] = [];
  roles: Role[] = [];
  selectedRole?: Role;
  showSuccessMessage = true;
  hasLoaded = false;

  activeTabChangedSub!: Subscription;

  availableBusinessUnits: BusinessUnit[] = [];
  sourcePicklist: BusinessUnit[] = [];
  targetPicklist: BusinessUnit[] = [];

  constructor() {
    super();
  }

  ngOnInit() {
    this._roleTableService.setLastLazyLoad(null);
    this.setTableService(this._roleTableService);
    this.roleBusinessUnitForm = this._fb.group({
      id: [null],
      name: [{ value: null, disabled: true }, Validators.required],
      businessUnits: [{ value: false, disabled: true }],
      version: [null],
    });

    this.initialize();
  }

  ngOnDestroy(): void {
    if (this.activeTabChangedSub) {
      this.activeTabChangedSub.unsubscribe();
    }
  }

  initialize() {
    const screenName$ = this._translateService.get(
      'ROLE.SCREEN.ROLE_BUSINESS_UNIT_ASSIGN'
    );
    const dialogHeader$ = this._translateService.get('ROLE.LABEL.ROLE_DETAILS');
    forkJoin([screenName$, dialogHeader$]).subscribe((label) => {
      this._tabService.updateActiveTabLabel(label[0]);
      this.screenName = label[0];
      this.dialogHeader = label[1];
    });

    this._adminApi.getAvailableBusinessUnits().subscribe((businessUnits) => {
      this.availableBusinessUnits = businessUnits;
    });

    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.ADMINISTRATION.ROLES.EDIT_ROLES,
    ]);

    if (this.canEdit) {
      this.roleBusinessUnitForm.enable();
    }

    this.columns = ROLES_BUSINESS_UNITS_TABLE_DEFINITION;
    this.applyPreviousData();
    this.applyPreviousFilters();
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    const query = {
      role: null,
      businessUnit: null,
    };

    if (filters.name) {
      query.role = (filters.name as FilterMetadata).value;
    }
    if (filters.businessUnits) {
      query.businessUnit = (filters.businessUnits as FilterMetadata).value;
    }

    this.queryNetwork(req, $event, query);
  }

  queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ) {
    req.page += 1;
    const sort = this.buildNetworkSortQuery($event, 'role');

    this._roleTableService.setLastSortField('role');
    this._roleTableService.setLastSortDirection(1);

    this._adminApi
      .queryForRolesAndBusinessUnits(req.pageSize, req.page, query, sort)
      .subscribe({
        next: (roleConnection) => {
          this.rolesAndBusinessUnits = roleConnection.items;
          this.totalRecords = roleConnection.totalRecords;
          this.elements = [...this.rolesAndBusinessUnits];
          this.loading = false;
          this.hasLoaded = true;
        },
        error: (error) => {
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'ADMIN.MESSAGES.ERROR.QUERY_ROLE_BUSINESS_UNITS',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
          this.loading = false;
        },
      });
  }

  //Double clicking on table row action:
  changeViewEvent($event: { data: Role }): void {
    this.selectedRole = $event.data;
    this.roleBusinessUnitForm.patchValue({
      id: $event.data.id,
      name: $event.data.name,
      businessUnits: $event.data.businessUnits,
      version: $event.data.version,
    });

    // Ensure that the name field is not editable
    this.roleBusinessUnitForm.controls['name'].disable();

    // update the sourceBusinessUnits
    // update the targetBusinessUnits
    this.sourcePicklist = [];
    this.targetPicklist = [];
    const roleBusinessUnits =
      this.roleBusinessUnitForm.controls['businessUnits'].value
        ?.split(',')
        .map((x: string) => x.trim()) ?? [];

    this.availableBusinessUnits.forEach((x) => {
      if (roleBusinessUnits.includes(x.name)) {
        this.targetPicklist.push(x);
      } else {
        this.sourcePicklist.push(x);
      }
    });

    this.sourcePicklist.sort((a, b) => sortByProperty(a, b, 'name'));
    this.targetPicklist.sort((a, b) => sortByProperty(a, b, 'name'));
    this.displayDialog = true;
  }

  clearForm(): void {
    this.roleBusinessUnitForm.reset();
  }

  dialogClosed(): void {
    this.clearForm();
    this.selectedRole = undefined;
    const audit = this.audit();
  }

  //refresh button in top right
  refresh(): void {
    this.lazyLoad({ lazy: this._roleTableService.getLastLazyLoad()! }); //this triggers infinite loop
  }

  //Save button in dialog popup
  saveRole(): void {
    if (!this.canEdit) {
      return;
    }

    if (
      !isNullOrUndefined(this.roleBusinessUnitForm) &&
      this.roleBusinessUnitForm.valid
    ) {
      let role = null;
      if (!isNullOrUndefined(this.selectedRole)) {
        role = this.selectedRole;
      } else {
        role = Object.assign({}, this.roleBusinessUnitForm.getRawValue());
      }
      const updateRoleInput: UpdateRoleInput | null =
        this._adminBuilder.buildRoleBusinessUnitsInput(
          role,
          this.targetPicklist
        );
      this.saving = true;
      this._adminApi
        .updateRole(updateRoleInput!.id, updateRoleInput)
        .subscribe({
          next: (result) => {
            let updatedRole = this.updateRolesAndBusinessUnits(result);
            this.changeViewEvent({ data: updatedRole });
            this.clearForm();
            this.displayDialog = false;
            this.saving = false;
          },
          error: (error) => {
            this.saving = false;
            console.error('Error updating role.', error);
          },
        });
    }
  }

  updateRolesAndBusinessUnits(role: Role): Role {
    // find the role in the list
    let index = this.rolesAndBusinessUnits.findIndex((x) => x.id == role.id);
    this.rolesAndBusinessUnits[index].businessUnits = role.businessUnits
      .map((c) => c.name)
      .join(', ');
    return this.rolesAndBusinessUnits[index];
  }
}
